import DocumentHeader from '@/components/DocumentHeader';
import { BASE_URL, useCloudflareImage } from '@/hooks/useCloudflareImage';
import Schema from '@/components/DocumentSchema';
import { getCompanyStockUrl, getPriceTypeText } from '../utils';
import { useEffect } from 'react';
import { useTracking } from 'react-tracking';
import { getReadableCountry } from '@/utilities/markets';
import { useCompanyStock } from '../hooks/useCompanyStock';
import type { WebPage } from 'schema-dts';
import type { ISO2, PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { Helmet } from 'react-helmet';
import { useSummaryV2 } from '@/hooks/useCompany/useSummaryV2';

export const PageMeta = () => {
  const company = useCompanyStock();
  const { trackEvent } = useTracking();

  useEffect(() => {
    trackEvent({
      type: 'track',
      action: 'landed',
      subject: 'page',
      company_id: company.companyId,
      unique_symbol: company.uniqueSymbol,
    });
  }, [trackEvent, company.companyId, company.uniqueSymbol]);

  const summary = useSummaryV2(company.canonicalUrl);

  const ogImage = useCloudflareImage({
    src: summary.status === 'success' ? summary.coverImage : '',
    width: 1200,
    height: 627,
    fit: 'cover',
  });

  // Avoid `?? ""` on a bunch of variables
  if (
    !company.shortName ||
    !company.name ||
    !company.countryISO ||
    !company.uniqueSymbol ||
    !company.tickerSymbol ||
    summary.status !== 'success'
  )
    return;

  const data = {
    priceTypeText: getPriceTypeText(company.exchangeCountryISO as ISO2),
    country: getReadableCountry(
      company.countryISO.toLowerCase() as PrimaryCountryISOElseGlobal
    ),
  };

  const meta = {
    title: `${company.shortName} (${company.uniqueSymbol}) ${data.priceTypeText.priceLabel}`,
    description: `Find the latest ${company.name} (${
      company.uniqueSymbol
    }) ${data.priceTypeText.securityLabel.toLowerCase()} price, share price history, and explore other investor’s opinions to make your investment decision.`,
    canonicalUrl: `${BASE_URL}${getCompanyStockUrl(company.uniqueSymbol)}`,
    mainHeading: `${company.shortName} ${data.priceTypeText.priceLabel}`,
    logoUrl: `${BASE_URL}/news/images/sws-logo.svg`,
  };
  return (
    <>
      <Helmet>
        {/* Facebook (Instagram, linkedIn etc) Meta Tags */}
        <meta property="og:url" content={meta.canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={ogImage} />

        {/* X Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="simplywall.st" />
        <meta property="twitter:url" content={meta.canonicalUrl} />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <DocumentHeader
        title={meta.title}
        description={meta.description}
        allowCrawlers
      >
        <link rel="canonical" href={meta.canonicalUrl} />
      </DocumentHeader>
      <Schema<WebPage>
        item={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: meta.title,
          description: meta.description,
          mainEntityOfPage: meta.canonicalUrl,
          url: meta.canonicalUrl,
          headline: meta.mainHeading,
          keywords: [
            company.shortName,
            company.name,
            'Stock price',
            'Share price',
            'Stock quote',
            'News',
            'Opinions',
            'Community',
          ],
          creator: meta.mainHeading,
          mentions: [
            {
              '@type': 'Corporation',
              name: company.name,
              tickerSymbol: company.tickerSymbol,
            },
          ],
          publisher: {
            '@type': 'NewsMediaOrganization',
            name: 'Simply Wall St',
            url: BASE_URL,
            logo: {
              '@type': 'ImageObject',
              url: meta.logoUrl,
            },
          },
        }}
      />
    </>
  );
};
